import React, { useState, useEffect } from "react";
import { getFirestore, doc, setDoc, getDoc, collection, getDocs } from "firebase/firestore";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Select, MenuItem, FormControl, Autocomplete, InputLabel, Typography, Snackbar, Paper, Checkbox, FormControlLabel, CircularProgress, IconButton, Tooltip, Box } from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import DeleteIcon from "@mui/icons-material/Delete";
import EventIcon from "@mui/icons-material/Event";
import MuiAlert from '@mui/material/Alert';
import moment from "moment";
import "moment/locale/es";
import { getCuadrillasBasic } from "../../services/cuadrillasService";
import { motivos } from "../../utils/default";

const firestore = getFirestore();

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddAsistencia = () => {
    const [nroCuadrilla, setNroCuadrilla] = useState('');
    const [idCuadrilla, setIdCuadrilla] = useState('');
    const [codCuadrilla, setCodCuadrilla] = useState('');
    const [mesSeleccionado, setMesSeleccionado] = useState(moment());
    const [asistencia, setAsistencia] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [justification, setJustification] = useState("");
    const [cuadrillas, setCuadrillas] = useState([]);
    const [diasObjetivo, setDiasObjetivo] = useState('');
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: '', open: false });
    const [nota, setNota] = useState('');
    const [diasBackup, setDiasBackup] = useState({});
    const [isBackupInDialog, setIsBackupInDialog] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openDiasObjetivoModal, setOpenDiasObjetivoModal] = useState(false);
    const [backupRangeDialog, setBackupRangeDialog] = useState({ open: false, cuadrillaId: null, from: 1, to: 1 });
    const [feriados, setFeriados] = useState({});
    const [hoveredCell, setHoveredCell] = useState({ row: -1, col: -1 });
    const [openFeriadosModal, setOpenFeriadosModal] = useState(false);

    useEffect(() => {
        getCuadrillasBasic(setCuadrillas)

        const cargarAsistencia = async () => {
            setAsistencia({});
            setDiasBackup({});
            setDiasObjetivo('');
            setFeriados({});

            const year = mesSeleccionado.year();
            const month = mesSeleccionado.format("MM");
            const asistenciasRef = collection(firestore, `asistencias/${year}/${month}`);

            const snapshot = await getDocs(asistenciasRef);

            const diasDelMes = mesSeleccionado.daysInMonth();
            const domingos = [];
            for (let d = 1; d <= diasDelMes; d++) {
                if (mesSeleccionado.clone().date(d).day() === 0) {
                    domingos.push(d);
                }
            }

            const nuevasDiasBackup = {};
            setAsistencia(() => {
                const nuevasAsistencias = {};

                snapshot.docs.forEach((docSnap) => {
                    if (docSnap.id !== "dias") {
                        const idCuadrilla = docSnap.id;
                        const data = docSnap.data();
                        const diasNoAsistidosFirestore = data.dias_no_asistidos || {};
                        const merged = {
                            ...diasNoAsistidosFirestore,
                        };
                        domingos.forEach(d => {
                            if (!merged[d]) {
                                merged[d] = { motivo: 'D', nota: '' };
                            }
                        });
                        nuevasAsistencias[idCuadrilla] = merged;
                        nuevasDiasBackup[idCuadrilla] = data.dias_backup || {};
                    }
                });
                return nuevasAsistencias;
            });
            setDiasBackup(nuevasDiasBackup);

            // Cargar el diasObjetivo y feriados existentes si ya fueron configurados para este mes
            const diasDocRef = doc(firestore, `asistencias/${year}/${month}/dias`);
            const diasDocSnap = await getDoc(diasDocRef);
            if (diasDocSnap.exists()) {
                const data = diasDocSnap.data();
                if (data.dias_objetivo) {
                    setDiasObjetivo(data.dias_objetivo);
                }
                setFeriados(data.feriados || {});
            } else {
                setFeriados({});
            }
        };

        cargarAsistencia();
    }, [mesSeleccionado]);

    useEffect(() => {
        if (!cuadrillas.length) return;
        const diasDelMes = mesSeleccionado.daysInMonth();
        const domingos = [];
        for (let d = 1; d <= diasDelMes; d++) {
            if (mesSeleccionado.clone().date(d).day() === 0) {
                domingos.push(d);
            }
        }
        const feriadosDias = Object.keys(feriados).map(Number);
        if (!domingos.length && !feriadosDias.length) return;
        setAsistencia(prev => {
            const next = { ...prev };
            cuadrillas
                .filter(c => c.id !== "0" && c.id !== "1" && c.id !== 0 && c.id !== 1)
                .forEach(c => {
                    const actual = { ...(next[c.id] || {}) };
                    let modificado = false;
                    domingos.forEach(d => {
                        if (!actual[d]) {
                            actual[d] = { motivo: 'D', nota: '' };
                            modificado = true;
                        }
                    });
                    feriadosDias.forEach(d => {
                        if (!actual[d] || actual[d].motivo === 'D' || actual[d].motivo === 'F') {
                            actual[d] = { motivo: 'F', nota: feriados[d] || '' };
                            modificado = true;
                        }
                    });
                    if (modificado) next[c.id] = actual;
                });
            return next;
        });
    }, [cuadrillas, mesSeleccionado, feriados]);

    const handleDayClick = (day, cuadrillaId) => {
        setSelectedDay(day);
        setIdCuadrilla(cuadrillaId);
        setIsBackupInDialog(!!diasBackup[cuadrillaId]?.[day]);

        const inasistenciaExistente = asistencia[cuadrillaId]?.[day];
        if (inasistenciaExistente) {
            const motivoEncontrado = motivos.find(m => m.resumen === inasistenciaExistente.motivo);
            if (motivoEncontrado) {
                setJustification(motivoEncontrado.resumen);
            }
            setNota(inasistenciaExistente.nota || '');
        } else {
            setJustification('');
            setNota('');
        }

        setOpenDialog(true);
    };

    const saveMotivo = () => {
        setDiasBackup((prev) => {
            const next = { ...prev };
            if (isBackupInDialog) {
                next[idCuadrilla] = { ...(next[idCuadrilla] || {}), [selectedDay]: true };
            } else if (next[idCuadrilla]) {
                const { [selectedDay]: _, ...rest } = next[idCuadrilla];
                next[idCuadrilla] = Object.keys(rest).length ? rest : undefined;
            }
            return next;
        });
        setAsistencia((prev) => {
            const next = { ...prev };
            if (justification || nota) {
                next[idCuadrilla] = { ...(next[idCuadrilla] || {}), [selectedDay]: { motivo: justification, nota: nota } };
            } else if (next[idCuadrilla]) {
                const { [selectedDay]: _, ...rest } = next[idCuadrilla];
                next[idCuadrilla] = Object.keys(rest).length ? rest : undefined;
            }
            return next;
        });
        setOpenDialog(false);
        setJustification('');
        setNota('');
    };

    const eliminarInasistencia = () => {
        const esDomingo = mesSeleccionado.clone().date(selectedDay).day() === 0;
        const esFeriado = !!feriados[selectedDay];

        setAsistencia((prev) => {
            const nuevaAsistencia = { ...prev };
            if (esDomingo || esFeriado) {
                // Si es domingo o feriado, marcamos como Trabajado ('T') en lugar de eliminar
                nuevaAsistencia[idCuadrilla] = {
                    ...(nuevaAsistencia[idCuadrilla] || {}),
                    [selectedDay]: { motivo: 'T', nota: '' }
                };
            } else {
                // Si es un día normal, eliminamos
                if (nuevaAsistencia[idCuadrilla]) {
                    const { [selectedDay]: removed, ...rest } = nuevaAsistencia[idCuadrilla];
                    nuevaAsistencia[idCuadrilla] = Object.keys(rest).length ? rest : undefined;
                }
            }
            return nuevaAsistencia;
        });
        setDiasBackup((prev) => {
            const next = { ...prev };
            if (next[idCuadrilla]) {
                const { [selectedDay]: _, ...rest } = next[idCuadrilla];
                next[idCuadrilla] = Object.keys(rest).length ? rest : undefined;
            }
            return next;
        });
        setOpenDialog(false);
        setJustification('');
        setNota('');
    };

    const saveDiasObjetivo = async () => {
        const year = mesSeleccionado.year();
        const month = mesSeleccionado.format("MM");
        const diasRef = doc(firestore, `asistencias/${year}/${month}/dias`);

        await setDoc(diasRef, { dias_objetivo: diasObjetivo, feriados }, { merge: true });

        console.log("✅ Días objetivo y feriados guardados correctamente.");
    };

    const eliminarFeriado = (dia) => {
        setFeriados(prev => {
            const { [dia]: _removed, ...rest } = prev;
            return rest;
        });
        setAsistencia(prev => {
            const next = { ...prev };
            const fecha = mesSeleccionado.clone().date(dia);
            const esDomingo = fecha.day() === 0;
            Object.keys(next).forEach(cuadrillaId => {
                if (next[cuadrillaId]?.[dia]?.motivo === 'F') {
                    const { [dia]: _r, ...rest } = next[cuadrillaId] || {};
                    if (esDomingo) rest[dia] = { motivo: 'D', nota: '' };
                    next[cuadrillaId] = Object.keys(rest).length > 0 ? rest : undefined;
                }
            });
            return next;
        });
    };

    const saveJustification = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const year = mesSeleccionado.year();
            const month = mesSeleccionado.format("MM");
            await saveDiasObjetivo();
            const cuadrillasToSave = cuadrillas.filter(c => c.id !== "0" && c.id !== "1" && c.id !== 0 && c.id !== 1);
            await Promise.all(cuadrillasToSave.map(async (cuadrilla) => {
                const cuadrillaAsistencia = asistencia[cuadrilla.id] || {};
                const cuadrillaDiasBackup = diasBackup[cuadrilla.id] || {};
                await setDoc(doc(firestore, `asistencias/${year}/${month}/${cuadrilla.id}`), {
                    idCuadrilla: cuadrilla.id,
                    nroCuadrilla: cuadrilla.numero,
                    usuarioCuadrilla: cuadrilla.usuario,
                    codCuadrilla: cuadrilla.codigo,
                    mes: month,
                    diasObjetivo: diasObjetivo,
                    dias_no_asistidos: cuadrillaAsistencia,
                    dias_backup: cuadrillaDiasBackup,
                }, { merge: true });
            }));
            setSnackbarInfo({ text: 'Guardado correctamente!', type: 'success', open: true });
        } finally {
            setSaving(false);
        }
    };


    const onChangeSelectCuadrilla = (event, cuadrilla) => {
        const nroCuadrilla = cuadrilla.numero;
        const idCuadrilla = cuadrilla.id;
        const codCuadrilla = cuadrilla?.codigo
        setNroCuadrilla(nroCuadrilla);
        setIdCuadrilla(idCuadrilla);
        setCodCuadrilla(codCuadrilla);
    }

    const onChangeSelectMotivos = (event, motivo) => {
        setJustification(motivo.resumen);
    }

    const onChangeNota = (event) => {
        setNota(event.target.value);
    }

    const handleChangeDiasObjetivo = (event) => {
        setDiasObjetivo(event.target.value);
    };

    const handleCloseSanckbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbarInfo({ text: '', type: 'info', open: false });
    };

    const applyBackupRange = () => {
        const { cuadrillaId, from, to } = backupRangeDialog;
        const start = Math.min(from, to);
        const end = Math.max(from, to);
        setDiasBackup((prev) => {
            const next = { ...prev, [cuadrillaId]: { ...(prev[cuadrillaId] || {}) } };
            for (let d = start; d <= end; d++) {
                next[cuadrillaId][d] = true;
            }
            return next;
        });
        setBackupRangeDialog({ open: false, cuadrillaId: null, from: 1, to: 1 });
    };

    const action = (
        <Button color="inherit" size="small" href='/asistencia-list'>
            Ver asistencias
        </Button>
    );

    return (
        <div>
            <Typography sx={{ margin: '20px' }} variant="h4">Registro de Asistencia</Typography>

             {/* <FormControl sx={{ width: "300px" }}>
                <Autocomplete
                    options={cuadrillas}
                    id="disable-clearable"
                    disableClearable
                    onChange={(e, cuadrilla) => onChangeSelectCuadrilla(e, cuadrilla)}
                    getOptionLabel={(option) => option.usuario ? `${option.usuario} - ${option.numero}` : (option.numero || '')}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    fullWidth
                    renderInput={(params) => (
                        <TextField {...params} label="Cuadrilla" placeholder="Cuadrilla" />
                    )}
                />
            </FormControl> */}
            <TextField
                type="month"
                value={mesSeleccionado.format("YYYY-MM")}
                onChange={(e) => setMesSeleccionado(moment(e.target.value))}
                style={{ marginLeft: "10px", marginBottom: "10px" }}
            />
            <Button
                variant="outlined"
                onClick={() => setOpenDiasObjetivoModal(true)}
                sx={{ margin: "10px 10px" }}
            >
                Días objetivo{diasObjetivo !== '' ? `: ${diasObjetivo}` : ''}
            </Button>
            <Button
                variant="outlined"
                startIcon={<EventIcon />}
                onClick={() => setOpenFeriadosModal(true)}
                sx={{ margin: "10px 10px" }}
            >
                Feriados ({Object.keys(feriados).length})
            </Button>
            <Dialog open={openDiasObjetivoModal} onClose={() => setOpenDiasObjetivoModal(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Días objetivo</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ marginTop: 2 }}>
                        <InputLabel id="dias-objetivo-label">Días objetivo</InputLabel>
                        <Select
                            labelId="dias-objetivo-label"
                            value={diasObjetivo ?? ''}
                            label="Días objetivo"
                            onChange={handleChangeDiasObjetivo}
                        >
                            {Array.from({ length: mesSeleccionado?.daysInMonth() ?? 31 }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDiasObjetivoModal(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            await saveDiasObjetivo();
                            setOpenDiasObjetivoModal(false);
                        }}
                        disabled={diasObjetivo === '' || diasObjetivo == null}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
            <Button
                disabled={!mesSeleccionado || diasObjetivo === '' || saving}
                variant="contained"
                onClick={saveJustification}
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
            >
                {saving ? 'Guardando...' : 'Guardar Asistencia'}
            </Button>

            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: '70vh',
                    overflow: 'auto',
                    '& .MuiTableCell-root': {
                        borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                        padding: '2px 4px',
                    },
                    '& .MuiTableCell-root:hover': { backgroundColor: '#e0e0e0' },
                    '& .MuiTableCell-root:last-child': {
                        borderRight: 'none'
                    }
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    backgroundColor: '#f5f5f5',
                                    fontWeight: 'bold',
                                    position: 'sticky',
                                    top: 0,
                                    left: 0,
                                    zIndex: 101,
                                    minWidth: '180px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <strong>Cuadrilla</strong>
                            </TableCell>
                            {Array.from({ length: mesSeleccionado.daysInMonth() }, (_, i) => {
                                const dayNum = i + 1;
                                const fecha = mesSeleccionado.clone().date(dayNum).locale('es');
                                const diaSemana = fecha.format('dd');
                                const diaCapitalizado = diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);
                                const esDomingo = fecha.day() === 0;
                                const esFinde = esDomingo || fecha.day() === 6;
                                const esFeriado = !!feriados[dayNum];
                                const headerSx = esFeriado
                                    ? { backgroundColor: '#c9b501 !important', color: '#827717' }
                                    : esDomingo
                                        ? { backgroundColor: '#f6882d !important', color: '#e65100' }
                                        : { backgroundColor: '#f5f5f5', color: 'inherit' };
                                return (
                                    <Tooltip key={i} title={esFeriado ? `Feriado: ${feriados[dayNum]}` : ''} arrow disableHoverListener={!esFeriado}>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                fontWeight: 'bold',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 100,
                                                minWidth: '36px',
                                                width: '36px',
                                                padding: '2px 4px',
                                                lineHeight: 1,
                                                ...headerSx,
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                                                <span style={{ fontSize: '0.6rem', opacity: 0.75, fontWeight: esFinde || esFeriado ? 700 : 400 }}>
                                                    {diaCapitalizado}
                                                </span>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                    {dayNum}
                                                </span>
                                            </div>
                                        </TableCell>
                                    </Tooltip>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody onMouseLeave={() => setHoveredCell({ row: -1, col: -1 })}>
                        {cuadrillas
                            .filter(cuadrilla => cuadrilla.id !== "0" && cuadrilla.id !== "1" && cuadrilla.id !== 0 && cuadrilla.id !== 1)
                            .map((cuadrilla, rowIndex) => {
                                const rowHovered = hoveredCell.row === rowIndex;
                                return (
                                    <TableRow key={cuadrilla.id} sx={{ height: '32px' }} onMouseEnter={() => setHoveredCell({ row: rowIndex, col: hoveredCell.col })}>
                                        <TableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1000,
                                                minWidth: '180px',
                                                backgroundColor: rowHovered ? '#d1d1d1' : '#ffffff',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <span style={{ fontSize: '0.7rem' }}>
                                                {cuadrilla.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla.numero}
                                            </span>
                                            <Tooltip title="Marcar backup por rango">
                                                <IconButton
                                                    size="small"
                                                    sx={{ ml: 0.5, p: '2px' }}
                                                    onClick={() => setBackupRangeDialog({ open: true, cuadrillaId: cuadrilla.id, from: 1, to: 1 })}
                                                >
                                                    <DateRangeIcon sx={{ fontSize: '0.9rem', color: '#1976d2' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        {Array.from({ length: mesSeleccionado.daysInMonth() }, (_, i) => {
                                            const day = i + 1;
                                            const esBackup = diasBackup[cuadrilla.id]?.[day];
                                            const esInasistencia = asistencia[cuadrilla.id]?.[day];
                                            const esDomingo = mesSeleccionado.clone().date(day).day() === 0;
                                            const esFeriado = !!feriados[day];
                                            const colHovered = hoveredCell.col === i;
                                            const isCross = rowHovered || colHovered;
                                            const isExact = rowHovered && colHovered;
                                            const esInasistenciaReal = esInasistencia && esInasistencia.motivo !== 'F' && esInasistencia.motivo !== 'D' && esInasistencia.motivo !== 'T';
                                            const esTrabajadoDomingoFeriado = esInasistencia && esInasistencia.motivo === 'T';
                                            const bg = esBackup ? "#e3f2fd"
                                                : (esFeriado && !esTrabajadoDomingoFeriado) ? "#fff176"
                                                    : (esDomingo && !esTrabajadoDomingoFeriado) ? "#ffcc80"
                                                        : esInasistenciaReal ? "#ffebee"
                                                            : "#e8f5e9";
                                            const bgHover = esBackup ? "#bbdefb"
                                                : (esFeriado && !esTrabajadoDomingoFeriado) ? "#fff59d"
                                                    : (esDomingo && !esTrabajadoDomingoFeriado) ? "#ffe0b2"
                                                        : esInasistenciaReal ? "#ffcdd2"
                                                            : "#c8e6c9";
                                            return (
                                                <TableCell
                                                    key={i}
                                                    onMouseEnter={() => setHoveredCell({ row: rowIndex, col: i })}
                                                    align="center"
                                                    sx={{
                                                        background: isExact ? '#bdbdbd' : isCross ? '#e0e0e0' : bg,
                                                        cursor: "pointer",
                                                        minWidth: '36px',
                                                        width: '36px',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        '&:hover': { backgroundColor: isExact ? '#bdbdbd' : bgHover }
                                                    }}
                                                    onClick={() => handleDayClick(day, cuadrilla.id)}
                                                >
                                                    <span style={{ fontSize: '0.7rem' }}>
                                                        {esInasistencia?.motivo === 'T' ? "✔" : (esInasistencia?.motivo || (esBackup ? "B" : "✔"))}
                                                    </span>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openFeriadosModal} onClose={() => setOpenFeriadosModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ textTransform: 'capitalize' }}>
                    Feriados de {mesSeleccionado.locale('es').format('MMMM YYYY')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 1 }}>
                        Hacé click en un día para marcarlo como feriado. Click en uno marcado para quitarlo.
                    </Typography>

                    <Box sx={{ maxWidth: 340, mx: 'auto' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
                            {['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'].map((d, idx) => (
                                <Box key={d} sx={{
                                    textAlign: 'center',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: idx >= 5 ? '#e65100' : 'text.secondary',
                                    py: 0.25,
                                }}>
                                    {d}
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                            {(() => {
                                const firstOffset = mesSeleccionado.clone().startOf('month').isoWeekday() - 1;
                                const totalDias = mesSeleccionado.daysInMonth();
                                const totalCeldas = Math.ceil((firstOffset + totalDias) / 7) * 7;
                                return Array.from({ length: totalCeldas }, (_, i) => {
                                    const dayNum = i - firstOffset + 1;
                                    if (dayNum < 1 || dayNum > totalDias) {
                                        return <Box key={i} sx={{ height: 36, visibility: 'hidden' }} />;
                                    }
                                    const fecha = mesSeleccionado.clone().date(dayNum);
                                    const esDomingo = fecha.day() === 0;
                                    const esFeriado = !!feriados[dayNum];
                                    return (
                                        <Tooltip
                                            key={i}
                                            title={esFeriado ? feriados[dayNum] : ''}
                                            arrow
                                            disableHoverListener={!esFeriado}
                                        >
                                            <Box
                                                onClick={() => {
                                                    if (esFeriado) {
                                                        eliminarFeriado(String(dayNum));
                                                    } else {
                                                        setFeriados(prev => ({
                                                            ...prev,
                                                            [dayNum]: 'Feriado',
                                                        }));
                                                    }
                                                }}
                                                sx={{
                                                    height: 36,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: esFeriado ? '#fbc02d' : esDomingo ? '#ffb74d' : '#e0e0e0',
                                                    backgroundColor: esFeriado ? '#fff59d' : esDomingo ? '#ffe0b2' : '#fff',
                                                    color: esFeriado ? '#827717' : esDomingo ? '#e65100' : 'inherit',
                                                    fontWeight: esFeriado ? 700 : 500,
                                                    fontSize: '0.85rem',
                                                    transition: 'all 0.15s ease',
                                                    userSelect: 'none',
                                                    '&:hover': {
                                                        backgroundColor: esFeriado ? '#fff176' : '#fff9c4',
                                                        borderColor: '#fbc02d',
                                                    },
                                                }}
                                            >
                                                {dayNum}
                                            </Box>
                                        </Tooltip>
                                    );
                                });
                            })()}
                        </Box>
                    </Box>

                    {Object.keys(feriados).length > 0 && (
                        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {Object.keys(feriados).length} feriado{Object.keys(feriados).length !== 1 ? 's' : ''} cargado{Object.keys(feriados).length !== 1 ? 's' : ''}
                            </Typography>
                            {Object.keys(feriados)
                                .sort((a, b) => Number(a) - Number(b))
                                .map(dia => (
                                    <Box key={dia} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                        <Typography variant="body2" sx={{ minWidth: 28, fontWeight: 600 }}>
                                            {dia}
                                        </Typography>
                                        <TextField
                                            size="small"
                                            variant="standard"
                                            value={feriados[dia]}
                                            onChange={(e) => setFeriados(prev => ({ ...prev, [dia]: e.target.value }))}
                                            sx={{ flex: 1 }}
                                            inputProps={{ style: { fontSize: '0.875rem' } }}
                                        />
                                        <IconButton size="small" color="error" onClick={() => eliminarFeriado(dia)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenFeriadosModal(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={backupRangeDialog.open} onClose={() => setBackupRangeDialog({ open: false, cuadrillaId: null, from: 1, to: 1 })} maxWidth="xs" fullWidth>
                <DialogTitle>Backup por rango de días</DialogTitle>
                <DialogContent sx={{ display: 'flex', gap: 2, pt: '16px !important' }}>
                    <FormControl fullWidth>
                        <InputLabel>Día desde</InputLabel>
                        <Select
                            value={backupRangeDialog.from}
                            label="Día desde"
                            onChange={(e) => setBackupRangeDialog(prev => ({ ...prev, from: e.target.value }))}
                        >
                            {Array.from({ length: mesSeleccionado.daysInMonth() }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Día hasta</InputLabel>
                        <Select
                            value={backupRangeDialog.to}
                            label="Día hasta"
                            onChange={(e) => setBackupRangeDialog(prev => ({ ...prev, to: e.target.value }))}
                        >
                            {Array.from({ length: mesSeleccionado.daysInMonth() }, (_, i) => (
                                <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBackupRangeDialog({ open: false, cuadrillaId: null, from: 1, to: 1 })}>Cancelar</Button>
                    <Button variant="contained" onClick={applyBackupRange}>Aplicar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openDialog} onClose={() => {
                setOpenDialog(false);
                setJustification('');
                setNota('');
            }}>
                <DialogTitle>
                    {asistencia[idCuadrilla]?.[selectedDay] || diasBackup[idCuadrilla]?.[selectedDay]
                        ? `Editar - Día ${selectedDay}` : `Registrar - Día ${selectedDay}`}
                </DialogTitle>
                <DialogContent>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isBackupInDialog}
                                onChange={(e) => setIsBackupInDialog(e.target.checked)}
                            />
                        }
                        label="Estuvo de backup este día"
                        sx={{ display: 'block', marginBottom: 1 }}
                    />
                    <FormControl sx={{ width: "200px" }}>
                        <Autocomplete
                            options={motivos}
                            id="disable-clearable"
                            disableClearable
                            value={motivos.find(m => m.resumen === justification) || null}
                            onChange={(e, motivo) => onChangeSelectMotivos(e, motivo)}
                            getOptionLabel={(option) => `${option.resumen} - ${option.motivo}`}
                            fullWidth
                            renderInput={(params) => (
                                <TextField {...params} label="Motivo inasistencia" placeholder="Motivo" />
                            )}
                        />
                    </FormControl>
                    <TextField
                        id="outlined-number"
                        label="Nota"
                        className="text-form__serial"
                        value={nota}
                        onChange={(e) => onChangeNota(e)}
                        name="nota"
                        InputLabelProps={{ shrink: true }}
                        sx={{ display: 'block', marginTop: '20px' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setOpenDialog(false);
                        setJustification('');
                        setNota('');
                    }}>Cancelar</Button>
                    {(asistencia[idCuadrilla]?.[selectedDay] || diasBackup[idCuadrilla]?.[selectedDay]) && (
                        <Button onClick={eliminarInasistencia} color="error">
                            Eliminar
                        </Button>
                    )}
                    <Button onClick={saveMotivo} color="primary">
                        {(asistencia[idCuadrilla]?.[selectedDay] || diasBackup[idCuadrilla]?.[selectedDay]) ? 'Actualizar' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarInfo.open}
                autoHideDuration={2000}
                onClose={handleCloseSanckbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                action={action}
            >
                <Alert onClose={handleCloseSanckbar} severity={snackbarInfo.type} sx={{ width: '100%' }} action={action}>
                    {snackbarInfo.text}
                </Alert>
             </Snackbar>
        </div>
    );
};

export default AddAsistencia;
